import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

import LoginForm from './components/LoginForm';
import TrustIndicators from './components/TrustIndicators';
import ProviderTestimonials from './components/ProviderTestimonials';
import SecurityFeatures from './components/SecurityFeatures';
import Icon from '../../components/AppIcon';
import MainNavigation from '../../components/ui/MainNavigation';

const LeverancierLogin = () => {
  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Navigation */}
      <MainNavigation />

      {/* Main Content */}
      <main className="flex-1 py-8 sm:py-12 lg:py-16 pt-16 lg:pt-12 lg:ml-[72px] transition-all duration-300">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 sm:gap-8 items-start">
            {/* Left Column - Login Form */}
            <div className="lg:col-span-5 xl:col-span-4">
              <LoginForm />
            </div>

            {/* Right Column - Trust & Testimonials */}
            <div className="lg:col-span-7 xl:col-span-8">
              <div className="space-y-6">
                {/* Welcome Section */}
                <div className="bg-gradient-to-r from-primary/10 to-secondary/10 rounded-lg border border-border p-6">
                  <div className="text-center mb-6">
                    <h2 className="text-2xl font-heading font-semibold text-foreground mb-3">
                      Welkom bij Verduurzaamwijzer
                    </h2>
                    <p className="text-base font-body text-muted-foreground">
                      Het grootste platform voor duurzaamheidsoplossingen in Nederland
                    </p>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
                    <div className="text-center">
                      <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-2">
                        <Icon name="Users" size={24} color="var(--color-primary)" />
                      </div>
                      <div className="text-lg font-heading font-semibold text-primary">2.500+</div>
                      <div className="text-sm font-body text-muted-foreground">Actieve Leveranciers</div>
                    </div>
                    <div className="text-center">
                      <div className="w-12 h-12 bg-secondary/10 rounded-full flex items-center justify-center mx-auto mb-2">
                        <Icon name="Home" size={24} color="var(--color-secondary)" />
                      </div>
                      <div className="text-lg font-heading font-semibold text-secondary">15.000+</div>
                      <div className="text-sm font-body text-muted-foreground">Projecten Voltooid</div>
                    </div>
                    <div className="text-center">
                      <div className="w-12 h-12 bg-success/10 rounded-full flex items-center justify-center mx-auto mb-2">
                        <Icon name="Star" size={24} color="var(--color-success)" />
                      </div>
                      <div className="text-lg font-heading font-semibold text-success">4.8/5</div>
                      <div className="text-sm font-body text-muted-foreground">Gemiddelde Rating</div>
                    </div>
                  </div>
                </div>

                {/* Two Column Layout for Desktop */}
                <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 sm:gap-6">
                  {/* Trust Indicators */}
                  <TrustIndicators />
                  
                  {/* Security Features */}
                  <SecurityFeatures />
                </div>

                {/* Leverancier Testimonials */}
                <ProviderTestimonials />
              </div>
            </div>
          </div>
        </div>
      </main>
      {/* Footer */}
      <footer className="bg-card border-t border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 sm:gap-6">
            {/* Company Info */}
            <div className="md:col-span-2">
              <div className="flex items-center space-x-2 mb-4">
                <div className="w-8 h-8 bg-primary rounded-md flex items-center justify-center">
                  <Icon name="Leaf" size={20} color="white" />
                </div>
                <span className="font-heading font-semibold text-xl text-foreground">
                  Verduurzaamwijzer
                </span>
              </div>
              <p className="text-sm font-body text-muted-foreground mb-4">
                Het vertrouwde platform voor duurzaamheidsoplossingen in Nederland. 
                Verbinden van leveranciers met bewuste consumenten sinds 2020.
              </p>
              <div className="flex items-center space-x-4">
                <div className="flex items-center space-x-1">
                  <Icon name="MapPin" size={14} color="var(--color-muted-foreground)" />
                  <span className="text-xs font-caption text-muted-foreground">Amsterdam, Nederland</span>
                </div>
                <div className="flex items-center space-x-1">
                  <Icon name="Phone" size={14} color="var(--color-muted-foreground)" />
                  <span className="text-xs font-caption text-muted-foreground">020-1234567</span>
                </div>
              </div>
            </div>

            {/* Quick Links */}
            <div>
              <h4 className="text-sm font-heading font-medium text-foreground mb-3">
                Voor Leveranciers
              </h4>
              <ul className="space-y-2">
                <li>
                  <Link to="/leverancier-registration" className="text-sm font-body text-muted-foreground hover:text-foreground transition-micro">
                    Registreren
                  </Link>
                </li>
                <li>
                  <Link to="/leverancier-login" className="text-sm font-body text-muted-foreground hover:text-foreground transition-micro">
                    Inloggen
                  </Link>
                </li>
                <li>
                  <a href="#" className="text-sm font-body text-muted-foreground hover:text-foreground transition-micro">
                    Tarieven
                  </a>
                </li>
                <li>
                  <a href="#" className="text-sm font-body text-muted-foreground hover:text-foreground transition-micro">
                    Support
                  </a>
                </li>
              </ul>
            </div>

            {/* Customer Links */}
            <div>
              <h4 className="text-sm font-heading font-medium text-foreground mb-3">
                Voor Klanten
              </h4>
              <ul className="space-y-2">
                <li>
                  <Link to="/customer-registration" className="text-sm font-body text-muted-foreground hover:text-foreground transition-micro">
                    Registreren
                  </Link>
                </li>
                <li>
                  <Link to="/customer-login" className="text-sm font-body text-muted-foreground hover:text-foreground transition-micro">
                    Inloggen
                  </Link>
                </li>
                <li>
                  <Link to="/homepage" className="text-sm font-body text-muted-foreground hover:text-foreground transition-micro">
                    Diensten Zoeken
                  </Link>
                </li>
                <li>
                  <a href="#" className="text-sm font-body text-muted-foreground hover:text-foreground transition-micro">
                    Help & FAQ
                  </a>
                </li>
              </ul>
            </div>
          </div>

          <div className="mt-6 sm:mt-8 pt-4 sm:pt-6 border-t border-border flex flex-col sm:flex-row justify-between items-center gap-4">
            <p className="text-xs font-caption text-muted-foreground">
              © {new Date()?.getFullYear()} Verduurzaamwijzer. Alle rechten voorbehouden.
            </p>
            <div className="flex items-center space-x-4 mt-4 sm:mt-0">
              <a href="#" className="text-xs font-caption text-muted-foreground hover:text-foreground transition-micro">
                Privacy
              </a>
              <a href="#" className="text-xs font-caption text-muted-foreground hover:text-foreground transition-micro">
                Voorwaarden
              </a>
              <a href="#" className="text-xs font-caption text-muted-foreground hover:text-foreground transition-micro">
                Cookies
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LeverancierLogin;